/**
 * AI Resilience Module
 * Vision 2.0 Phase 5: Help users future-proof their careers against AI displacement
 *
 * Features:
 * - Automation risk scores for careers
 * - AI augmentation recommendations
 * - Future-proof skill development paths
 * - AI-resistant career attributes
 */

/**
 * Automation risk level
 * Based on research from Oxford, McKinsey, and World Economic Forum
 */
export type AutomationRiskLevel = 'very_low' | 'low' | 'medium' | 'high' | 'very_high';

/**
 * AI relationship type - how the career interacts with AI
 */
export type AIRelationship =
  | 'ai_resistant'      // Jobs that are fundamentally hard for AI to do
  | 'ai_augmented'      // Jobs enhanced by AI tools
  | 'ai_collaborative'  // Jobs that work alongside AI
  | 'ai_vulnerable'     // Jobs at risk of AI replacement
  | 'ai_created';       // New jobs created by AI industry

/**
 * Skill category for future-proofing
 */
export type FutureProofSkillCategory =
  | 'human_connection'     // Empathy, emotional intelligence, care
  | 'creative_thinking'    // Novel ideas, artistic expression
  | 'complex_reasoning'    // Nuanced judgment, ethical decisions
  | 'physical_dexterity'   // Fine motor skills, unpredictable environments
  | 'ai_collaboration'     // Prompt engineering, AI tool mastery
  | 'domain_expertise'     // Deep specialized knowledge
  | 'leadership'           // Team motivation, vision setting
  | 'adaptability';        // Learning agility, change management

/**
 * AI tool that can augment a career
 */
export interface AITool {
  id: string;
  name: string;
  category: 'generative' | 'analytical' | 'automation' | 'communication' | 'specialized';
  description: string;
  learning_difficulty: 'easy' | 'medium' | 'hard';
  time_to_proficiency: string;
  free_tier_available: boolean;
  use_cases: string[];
}

/**
 * Future-proof skill recommendation
 */
export interface FutureProofSkill {
  id: string;
  name: string;
  category: FutureProofSkillCategory;
  description: string;
  why_future_proof: string;
  how_to_develop: string[];
  time_to_develop: string;
  ai_cant_replace_because: string;
}

/**
 * Career AI resilience profile
 */
export interface CareerAIProfile {
  career_id: string;
  automation_risk_score: number; // 0-100 (0 = very safe, 100 = very vulnerable)
  automation_risk_level: AutomationRiskLevel;
  ai_relationship: AIRelationship;

  // What makes this career resilient (or not)
  resilience_factors: string[];
  vulnerability_factors: string[];

  // Skills that make this career future-proof
  protective_skills: string[];

  // AI tools that enhance this career
  augmentation_tools: string[];

  // Recommended skills to develop
  future_proof_skills: string[];

  // Timeline for AI impact
  ai_impact_timeline: 'immediate' | '2-5_years' | '5-10_years' | '10+_years' | 'unlikely';

  // Career evolution recommendation
  evolution_path?: string;
}

/**
 * AI augmentation recommendation
 */
export interface AIAugmentationRec {
  career_id: string;
  tools: AITool[];
  skills_to_develop: FutureProofSkill[];
  productivity_boost_estimate: string; // e.g., "20-40% more efficient"
  competitive_advantage: string;
}

// ============================================
// AI TOOLS DATABASE
// ============================================

export const AI_TOOLS: Record<string, AITool> = {
  // Generative AI
  chatgpt: {
    id: 'chatgpt',
    name: 'ChatGPT',
    category: 'generative',
    description: 'Conversational AI for writing, brainstorming, and problem-solving',
    learning_difficulty: 'easy',
    time_to_proficiency: '1-2 weeks',
    free_tier_available: true,
    use_cases: ['Writing assistance', 'Research', 'Code help', 'Brainstorming'],
  },
  claude: {
    id: 'claude',
    name: 'Claude',
    category: 'generative',
    description: 'AI assistant for analysis, writing, and complex reasoning',
    learning_difficulty: 'easy',
    time_to_proficiency: '1-2 weeks',
    free_tier_available: true,
    use_cases: ['Document analysis', 'Writing', 'Research', 'Coding'],
  },
  midjourney: {
    id: 'midjourney',
    name: 'Midjourney',
    category: 'generative',
    description: 'AI image generation for creative and design work',
    learning_difficulty: 'medium',
    time_to_proficiency: '2-4 weeks',
    free_tier_available: false,
    use_cases: ['Concept art', 'Marketing visuals', 'Mood boards', 'Design exploration'],
  },
  dalle: {
    id: 'dalle',
    name: 'DALL-E',
    category: 'generative',
    description: 'AI image generation integrated with ChatGPT',
    learning_difficulty: 'easy',
    time_to_proficiency: '1 week',
    free_tier_available: true,
    use_cases: ['Quick visuals', 'Social media graphics', 'Presentations'],
  },

  // Analytical AI
  copilot: {
    id: 'copilot',
    name: 'GitHub Copilot',
    category: 'analytical',
    description: 'AI coding assistant for developers',
    learning_difficulty: 'medium',
    time_to_proficiency: '2-4 weeks',
    free_tier_available: false,
    use_cases: ['Code completion', 'Bug fixing', 'Documentation', 'Testing'],
  },
  tableau_ai: {
    id: 'tableau_ai',
    name: 'Tableau AI',
    category: 'analytical',
    description: 'AI-powered data visualization and insights',
    learning_difficulty: 'medium',
    time_to_proficiency: '4-8 weeks',
    free_tier_available: false,
    use_cases: ['Data analysis', 'Automated insights', 'Predictive analytics'],
  },

  // Automation AI
  zapier_ai: {
    id: 'zapier_ai',
    name: 'Zapier AI',
    category: 'automation',
    description: 'AI-powered workflow automation',
    learning_difficulty: 'easy',
    time_to_proficiency: '1-2 weeks',
    free_tier_available: true,
    use_cases: ['Email automation', 'Data entry', 'Task scheduling', 'Notifications'],
  },
  notion_ai: {
    id: 'notion_ai',
    name: 'Notion AI',
    category: 'automation',
    description: 'AI writing and organization within Notion',
    learning_difficulty: 'easy',
    time_to_proficiency: '1 week',
    free_tier_available: true,
    use_cases: ['Note summarization', 'Writing drafts', 'Task management'],
  },

  // Communication AI
  grammarly: {
    id: 'grammarly',
    name: 'Grammarly',
    category: 'communication',
    description: 'AI writing assistant for grammar, tone, and clarity',
    learning_difficulty: 'easy',
    time_to_proficiency: '1 week',
    free_tier_available: true,
    use_cases: ['Email writing', 'Professional documents', 'Tone adjustment'],
  },
  otter_ai: {
    id: 'otter_ai',
    name: 'Otter.ai',
    category: 'communication',
    description: 'AI meeting transcription and notes',
    learning_difficulty: 'easy',
    time_to_proficiency: '1 week',
    free_tier_available: true,
    use_cases: ['Meeting notes', 'Transcription', 'Action items'],
  },

  // Specialized AI
  canva_ai: {
    id: 'canva_ai',
    name: 'Canva AI',
    category: 'specialized',
    description: 'AI-powered design tools within Canva',
    learning_difficulty: 'easy',
    time_to_proficiency: '1-2 weeks',
    free_tier_available: true,
    use_cases: ['Social media graphics', 'Presentations', 'Marketing materials'],
  },
  jasper: {
    id: 'jasper',
    name: 'Jasper AI',
    category: 'specialized',
    description: 'AI marketing copy and content generation',
    learning_difficulty: 'easy',
    time_to_proficiency: '2 weeks',
    free_tier_available: false,
    use_cases: ['Marketing copy', 'Blog posts', 'Ad copy', 'Product descriptions'],
  },
  cursor: {
    id: 'cursor',
    name: 'Cursor',
    category: 'specialized',
    description: 'AI-first code editor with deep integration',
    learning_difficulty: 'medium',
    time_to_proficiency: '2-4 weeks',
    free_tier_available: true,
    use_cases: ['Full codebase editing', 'Refactoring', 'Documentation'],
  },
};

// ============================================
// FUTURE-PROOF SKILLS DATABASE
// ============================================

export const FUTURE_PROOF_SKILLS: Record<string, FutureProofSkill> = {
  // Human Connection
  emotional_intelligence: {
    id: 'emotional_intelligence',
    name: 'Emotional Intelligence',
    category: 'human_connection',
    description: 'Understanding and managing emotions in yourself and others',
    why_future_proof: 'AI cannot genuinely feel or deeply understand human emotions',
    how_to_develop: [
      'Practice active listening daily',
      'Seek feedback on your interpersonal interactions',
      'Study psychology and human behavior',
      'Mentor or coach others',
    ],
    time_to_develop: '6-12 months of deliberate practice',
    ai_cant_replace_because: 'Requires genuine human experience and authentic emotional connection',
  },
  empathetic_care: {
    id: 'empathetic_care',
    name: 'Empathetic Care',
    category: 'human_connection',
    description: 'Providing genuine care and support to people in need',
    why_future_proof: 'People need human touch and authentic compassion',
    how_to_develop: [
      'Volunteer in caregiving roles',
      'Take courses in counseling or care',
      'Practice presence and deep listening',
    ],
    time_to_develop: '3-6 months',
    ai_cant_replace_because: 'Human care requires physical presence, touch, and genuine emotional bond',
  },
  relationship_building: {
    id: 'relationship_building',
    name: 'Relationship Building',
    category: 'human_connection',
    description: 'Creating and nurturing professional and personal relationships',
    why_future_proof: 'Trust is built through human-to-human interaction',
    how_to_develop: [
      'Network intentionally',
      'Follow up consistently',
      'Provide value before asking',
      'Be genuinely curious about others',
    ],
    time_to_develop: 'Ongoing skill',
    ai_cant_replace_because: 'Authentic relationships require shared experiences and vulnerability',
  },

  // Creative Thinking
  creative_problem_solving: {
    id: 'creative_problem_solving',
    name: 'Creative Problem Solving',
    category: 'creative_thinking',
    description: 'Finding novel solutions to complex, ambiguous problems',
    why_future_proof: 'AI generates based on patterns; humans imagine the unprecedented',
    how_to_develop: [
      'Practice brainstorming techniques (SCAMPER, mind mapping)',
      'Study problems from multiple disciplines',
      'Embrace constraints as creative fuel',
      'Build diverse experiences',
    ],
    time_to_develop: '3-6 months',
    ai_cant_replace_because: 'True creativity requires consciousness, intention, and original thought',
  },
  artistic_expression: {
    id: 'artistic_expression',
    name: 'Artistic Expression',
    category: 'creative_thinking',
    description: 'Creating art that resonates emotionally with audiences',
    why_future_proof: 'Art reflects human experience; AI creates but doesn\'t experience',
    how_to_develop: [
      'Practice your chosen art form daily',
      'Study the masters in your field',
      'Share your work and gather feedback',
      'Draw from personal experience',
    ],
    time_to_develop: 'Lifetime pursuit',
    ai_cant_replace_because: 'Meaningful art requires lived experience, intention, and human perspective',
  },

  // Complex Reasoning
  ethical_judgment: {
    id: 'ethical_judgment',
    name: 'Ethical Judgment',
    category: 'complex_reasoning',
    description: 'Making nuanced moral decisions in complex situations',
    why_future_proof: 'Ethics requires human values, context, and accountability',
    how_to_develop: [
      'Study ethics and philosophy',
      'Analyze ethical dilemmas in your field',
      'Discuss moral questions with diverse groups',
      'Practice considering multiple stakeholder perspectives',
    ],
    time_to_develop: '6-12 months',
    ai_cant_replace_because: 'Moral decisions require human values, accountability, and conscience',
  },
  strategic_thinking: {
    id: 'strategic_thinking',
    name: 'Strategic Thinking',
    category: 'complex_reasoning',
    description: 'Long-term planning with incomplete information',
    why_future_proof: 'Strategy requires judgment about human behavior and values',
    how_to_develop: [
      'Study business strategy and case studies',
      'Practice scenario planning',
      'Learn from experienced strategists',
      'Play strategic games (chess, Go)',
    ],
    time_to_develop: '1-2 years',
    ai_cant_replace_because: 'Requires understanding of human motivation and societal change',
  },

  // Physical Dexterity
  fine_motor_skills: {
    id: 'fine_motor_skills',
    name: 'Fine Motor Skills',
    category: 'physical_dexterity',
    description: 'Precise physical manipulation in unpredictable environments',
    why_future_proof: 'Robots struggle with varied, unstructured physical tasks',
    how_to_develop: [
      'Practice hands-on crafts or trades',
      'Work with different materials and tools',
      'Develop muscle memory through repetition',
    ],
    time_to_develop: '6-24 months depending on complexity',
    ai_cant_replace_because: 'Physical dexterity in varied environments remains extremely challenging for robots',
  },

  // AI Collaboration
  prompt_engineering: {
    id: 'prompt_engineering',
    name: 'Prompt Engineering',
    category: 'ai_collaboration',
    description: 'Effectively communicating with AI to get optimal results',
    why_future_proof: 'As AI grows, those who direct it well become more valuable',
    how_to_develop: [
      'Experiment with different AI tools daily',
      'Study prompt patterns and techniques',
      'Join AI communities and learn from others',
      'Practice iterating on prompts',
    ],
    time_to_develop: '1-3 months',
    ai_cant_replace_because: 'Humans define what "good" output looks like',
  },
  ai_tool_orchestration: {
    id: 'ai_tool_orchestration',
    name: 'AI Tool Orchestration',
    category: 'ai_collaboration',
    description: 'Combining multiple AI tools to create powerful workflows',
    why_future_proof: 'Someone needs to architect and maintain AI systems',
    how_to_develop: [
      'Learn multiple AI tools across categories',
      'Practice building automated workflows',
      'Stay current on new AI developments',
      'Experiment with tool combinations',
    ],
    time_to_develop: '3-6 months',
    ai_cant_replace_because: 'Requires understanding business needs and human judgment',
  },

  // Domain Expertise
  deep_specialization: {
    id: 'deep_specialization',
    name: 'Deep Domain Expertise',
    category: 'domain_expertise',
    description: 'Expert-level knowledge in a specific field',
    why_future_proof: 'Experts validate, guide, and apply AI outputs',
    how_to_develop: [
      'Focus on a niche area for 5+ years',
      'Contribute to your field (publications, talks)',
      'Mentor others in your specialty',
      'Stay current on developments',
    ],
    time_to_develop: '5-10 years',
    ai_cant_replace_because: 'Expertise includes tacit knowledge and judgment AI cannot access',
  },

  // Leadership
  vision_setting: {
    id: 'vision_setting',
    name: 'Vision Setting',
    category: 'leadership',
    description: 'Defining compelling futures that inspire action',
    why_future_proof: 'AI executes; humans decide what\'s worth doing',
    how_to_develop: [
      'Study visionary leaders',
      'Practice articulating your own vision',
      'Learn storytelling and communication',
      'Take on leadership roles',
    ],
    time_to_develop: '2-5 years',
    ai_cant_replace_because: 'Vision requires human values, dreams, and moral compass',
  },
  team_motivation: {
    id: 'team_motivation',
    name: 'Team Motivation',
    category: 'leadership',
    description: 'Inspiring and energizing people to achieve goals',
    why_future_proof: 'People are motivated by other people, not machines',
    how_to_develop: [
      'Lead teams and projects',
      'Study motivational psychology',
      'Practice public speaking',
      'Learn to give effective feedback',
    ],
    time_to_develop: '1-3 years',
    ai_cant_replace_because: 'Motivation requires authentic human connection and inspiration',
  },

  // Adaptability
  learning_agility: {
    id: 'learning_agility',
    name: 'Learning Agility',
    category: 'adaptability',
    description: 'Rapidly acquiring new skills and knowledge',
    why_future_proof: 'The ability to learn is more valuable than any single skill',
    how_to_develop: [
      'Practice learning new things regularly',
      'Develop meta-learning skills',
      'Embrace discomfort of being a beginner',
      'Build systems for capturing knowledge',
    ],
    time_to_develop: 'Ongoing',
    ai_cant_replace_because: 'Adaptability is about responding to novel situations with judgment',
  },
  change_management: {
    id: 'change_management',
    name: 'Change Management',
    category: 'adaptability',
    description: 'Helping organizations navigate transitions',
    why_future_proof: 'Humans need other humans to guide them through change',
    how_to_develop: [
      'Study change management frameworks',
      'Lead transformation initiatives',
      'Learn organizational psychology',
      'Practice stakeholder management',
    ],
    time_to_develop: '2-4 years',
    ai_cant_replace_because: 'Change is emotional; it requires human trust and support',
  },
};

// ============================================
// CAREER AI PROFILES DATABASE
// ============================================

export const CAREER_AI_PROFILES: Record<string, CareerAIProfile> = {
  // High AI Resistance (Human-Centric Roles)
  personal_trainer: {
    career_id: 'personal_trainer',
    automation_risk_score: 15,
    automation_risk_level: 'very_low',
    ai_relationship: 'ai_augmented',
    resilience_factors: [
      'Physical presence required',
      'Motivation through human connection',
      'Adapting to individual body language',
      'Emotional support during struggle',
    ],
    vulnerability_factors: [
      'AI can generate workout plans',
      'Wearables track some metrics',
    ],
    protective_skills: ['Empathetic care', 'Motivation', 'Physical demonstration'],
    augmentation_tools: ['chatgpt', 'notion_ai'],
    future_proof_skills: ['emotional_intelligence', 'relationship_building', 'fine_motor_skills'],
    ai_impact_timeline: 'unlikely',
    evolution_path: 'Combine fitness expertise with wellness coaching and AI-powered tracking for holistic health programs',
  },

  social_worker: {
    career_id: 'social_worker',
    automation_risk_score: 12,
    automation_risk_level: 'very_low',
    ai_relationship: 'ai_augmented',
    resilience_factors: [
      'Crisis intervention requires human judgment',
      'Building trust with vulnerable populations',
      'Navigating complex family dynamics',
      'Advocating for clients',
    ],
    vulnerability_factors: [
      'Paperwork can be automated',
      'Some scheduling automated',
    ],
    protective_skills: ['Empathy', 'Crisis intervention', 'Cultural sensitivity'],
    augmentation_tools: ['chatgpt', 'grammarly', 'otter_ai'],
    future_proof_skills: ['empathetic_care', 'ethical_judgment', 'relationship_building'],
    ai_impact_timeline: 'unlikely',
  },

  registered_nurse: {
    career_id: 'registered_nurse',
    automation_risk_score: 18,
    automation_risk_level: 'very_low',
    ai_relationship: 'ai_augmented',
    resilience_factors: [
      'Physical patient care',
      'Rapid situational assessment',
      'Emotional support for patients',
      'Complex medical judgment',
    ],
    vulnerability_factors: [
      'Documentation may be automated',
      'Some monitoring automated',
    ],
    protective_skills: ['Clinical judgment', 'Patient communication', 'Emergency response'],
    augmentation_tools: ['chatgpt', 'otter_ai'],
    future_proof_skills: ['empathetic_care', 'ethical_judgment', 'fine_motor_skills'],
    ai_impact_timeline: 'unlikely',
  },

  electrician: {
    career_id: 'electrician',
    automation_risk_score: 22,
    automation_risk_level: 'low',
    ai_relationship: 'ai_augmented',
    resilience_factors: [
      'Work in unpredictable environments',
      'Troubleshooting unique problems',
      'Physical dexterity in tight spaces',
      'Customer interaction',
    ],
    vulnerability_factors: [
      'Smart home automation reduces some calls',
    ],
    protective_skills: ['Troubleshooting', 'Physical skills', 'Code compliance'],
    augmentation_tools: ['chatgpt'],
    future_proof_skills: ['fine_motor_skills', 'creative_problem_solving'],
    ai_impact_timeline: '10+_years',
    evolution_path: 'Specialize in smart home installation, EV charging, or solar integration',
  },

  plumber: {
    career_id: 'plumber',
    automation_risk_score: 20,
    automation_risk_level: 'low',
    ai_relationship: 'ai_resistant',
    resilience_factors: [
      'Each job site is unique',
      'Problem-solving in the moment',
      'Physical work in varied spaces',
      'Emergency repairs',
    ],
    vulnerability_factors: [],
    protective_skills: ['Diagnostics', 'Physical dexterity', 'Customer service'],
    augmentation_tools: ['chatgpt'],
    future_proof_skills: ['fine_motor_skills', 'creative_problem_solving'],
    ai_impact_timeline: 'unlikely',
  },

  // Medium AI Resistance (Augmented Roles)
  graphic_designer: {
    career_id: 'graphic_designer',
    automation_risk_score: 45,
    automation_risk_level: 'medium',
    ai_relationship: 'ai_augmented',
    resilience_factors: [
      'Client relationship management',
      'Brand strategy understanding',
      'Creative direction',
      'Quality control and refinement',
    ],
    vulnerability_factors: [
      'AI can generate initial designs',
      'Template-based work at risk',
      'Stock imagery being replaced',
    ],
    protective_skills: ['Brand strategy', 'Creative direction', 'Client communication'],
    augmentation_tools: ['midjourney', 'dalle', 'canva_ai', 'chatgpt'],
    future_proof_skills: ['artistic_expression', 'creative_problem_solving', 'ai_tool_orchestration'],
    ai_impact_timeline: '2-5_years',
    evolution_path: 'Become an AI-augmented creative director who uses AI for ideation but adds human insight and brand understanding',
  },

  copywriter: {
    career_id: 'copywriter',
    automation_risk_score: 55,
    automation_risk_level: 'medium',
    ai_relationship: 'ai_augmented',
    resilience_factors: [
      'Understanding brand voice',
      'Persuasive strategy',
      'Emotional connection with audience',
      'Editing and refinement',
    ],
    vulnerability_factors: [
      'AI can generate first drafts',
      'Simple copy is automatable',
      'SEO content at high risk',
    ],
    protective_skills: ['Strategy', 'Brand voice', 'Emotional resonance'],
    augmentation_tools: ['chatgpt', 'claude', 'jasper', 'grammarly'],
    future_proof_skills: ['creative_problem_solving', 'prompt_engineering', 'strategic_thinking'],
    ai_impact_timeline: '2-5_years',
    evolution_path: 'Transition to AI-augmented content strategist who directs AI tools while adding human insight',
  },

  web_developer: {
    career_id: 'web_developer',
    automation_risk_score: 48,
    automation_risk_level: 'medium',
    ai_relationship: 'ai_collaborative',
    resilience_factors: [
      'Complex system architecture',
      'Understanding business requirements',
      'Debugging novel issues',
      'Code review and quality',
    ],
    vulnerability_factors: [
      'AI can write basic code',
      'Simple CRUD apps automatable',
      'Boilerplate generation automated',
    ],
    protective_skills: ['Architecture', 'Problem-solving', 'Business understanding'],
    augmentation_tools: ['copilot', 'cursor', 'chatgpt', 'claude'],
    future_proof_skills: ['creative_problem_solving', 'ai_tool_orchestration', 'strategic_thinking'],
    ai_impact_timeline: '5-10_years',
    evolution_path: 'Focus on system design, AI integration, and complex problem-solving that requires deep understanding',
  },

  customer_service_representative: {
    career_id: 'customer_service_representative',
    automation_risk_score: 68,
    automation_risk_level: 'high',
    ai_relationship: 'ai_vulnerable',
    resilience_factors: [
      'Complex emotional situations',
      'VIP customer handling',
      'Escalation management',
    ],
    vulnerability_factors: [
      'Chatbots handle simple queries',
      'AI voice assistants improving',
      'Scripts are automatable',
    ],
    protective_skills: ['Escalation handling', 'Emotional de-escalation', 'Complex problem-solving'],
    augmentation_tools: ['chatgpt', 'grammarly'],
    future_proof_skills: ['emotional_intelligence', 'creative_problem_solving'],
    ai_impact_timeline: '2-5_years',
    evolution_path: 'Specialize in complex escalations, VIP accounts, or transition to customer success management',
  },

  // High AI Vulnerability (At-Risk Roles)
  data_entry: {
    career_id: 'data_entry',
    automation_risk_score: 92,
    automation_risk_level: 'very_high',
    ai_relationship: 'ai_vulnerable',
    resilience_factors: [
      'Handling exceptions',
      'Quality verification',
    ],
    vulnerability_factors: [
      'Routine data entry is highly automatable',
      'OCR and AI extraction improving',
      'Robotic process automation',
    ],
    protective_skills: ['Quality control', 'Exception handling'],
    augmentation_tools: ['zapier_ai'],
    future_proof_skills: ['ai_tool_orchestration', 'learning_agility'],
    ai_impact_timeline: 'immediate',
    evolution_path: 'Transition to data analysis, RPA administration, or process improvement roles',
  },

  bookkeeper: {
    career_id: 'bookkeeper',
    automation_risk_score: 75,
    automation_risk_level: 'high',
    ai_relationship: 'ai_vulnerable',
    resilience_factors: [
      'Client relationship',
      'Complex categorization decisions',
      'Advisory role',
    ],
    vulnerability_factors: [
      'Basic bookkeeping automated',
      'AI categorization improving',
      'Bank feeds automatic',
    ],
    protective_skills: ['Advisory', 'Complex judgment', 'Client communication'],
    augmentation_tools: ['chatgpt', 'notion_ai'],
    future_proof_skills: ['strategic_thinking', 'relationship_building', 'ai_tool_orchestration'],
    ai_impact_timeline: '2-5_years',
    evolution_path: 'Transition to advisory accounting, CFO services, or financial analysis',
  },

  // New AI-Created Roles
  prompt_engineer: {
    career_id: 'prompt_engineer',
    automation_risk_score: 30,
    automation_risk_level: 'low',
    ai_relationship: 'ai_created',
    resilience_factors: [
      'New field with evolving best practices',
      'Requires understanding of human needs',
      'Creative problem-solving',
      'Quality judgment',
    ],
    vulnerability_factors: [
      'AI may improve at self-prompting',
      'Prompt libraries reduce need',
    ],
    protective_skills: ['AI expertise', 'Communication', 'Domain knowledge'],
    augmentation_tools: ['chatgpt', 'claude', 'midjourney', 'cursor'],
    future_proof_skills: ['prompt_engineering', 'ai_tool_orchestration', 'creative_problem_solving'],
    ai_impact_timeline: '5-10_years',
    evolution_path: 'Evolve into AI systems architect or AI product manager',
  },

  ai_trainer: {
    career_id: 'ai_trainer',
    automation_risk_score: 25,
    automation_risk_level: 'low',
    ai_relationship: 'ai_created',
    resilience_factors: [
      'Human judgment for quality',
      'Understanding nuance and context',
      'Identifying edge cases',
    ],
    vulnerability_factors: [
      'Some training becomes automated',
    ],
    protective_skills: ['Domain expertise', 'Quality judgment', 'Nuanced understanding'],
    augmentation_tools: ['chatgpt', 'claude'],
    future_proof_skills: ['deep_specialization', 'ethical_judgment', 'learning_agility'],
    ai_impact_timeline: '5-10_years',
  },

  // Tutoring (augmented but human-essential)
  tutor: {
    career_id: 'tutor',
    automation_risk_score: 35,
    automation_risk_level: 'low',
    ai_relationship: 'ai_augmented',
    resilience_factors: [
      'Emotional support and encouragement',
      'Adapting to student confusion',
      'Building student confidence',
      'Understanding individual learning styles',
    ],
    vulnerability_factors: [
      'AI tutoring tools emerging',
      'Content delivery automatable',
    ],
    protective_skills: ['Patience', 'Adaptability', 'Emotional connection'],
    augmentation_tools: ['chatgpt', 'notion_ai'],
    future_proof_skills: ['emotional_intelligence', 'creative_problem_solving', 'ai_tool_orchestration'],
    ai_impact_timeline: '5-10_years',
    evolution_path: 'Become an AI-augmented learning coach who uses AI tools to personalize education',
  },

  // Technical Writer (augmented)
  technical_writer: {
    career_id: 'technical_writer',
    automation_risk_score: 52,
    automation_risk_level: 'medium',
    ai_relationship: 'ai_augmented',
    resilience_factors: [
      'Understanding complex systems',
      'User empathy',
      'Information architecture',
      'Stakeholder communication',
    ],
    vulnerability_factors: [
      'AI can generate first drafts',
      'Simple documentation automatable',
    ],
    protective_skills: ['Technical understanding', 'User research', 'Information design'],
    augmentation_tools: ['chatgpt', 'claude', 'grammarly', 'notion_ai'],
    future_proof_skills: ['creative_problem_solving', 'ai_tool_orchestration', 'deep_specialization'],
    ai_impact_timeline: '2-5_years',
    evolution_path: 'Focus on AI-assisted documentation workflows and developer experience',
  },

  // Video Editor (augmented)
  video_editor: {
    career_id: 'video_editor',
    automation_risk_score: 40,
    automation_risk_level: 'medium',
    ai_relationship: 'ai_augmented',
    resilience_factors: [
      'Storytelling judgment',
      'Creative pacing and flow',
      'Client collaboration',
      'Artistic vision',
    ],
    vulnerability_factors: [
      'AI can do basic cuts',
      'Auto-color correction',
      'Simple montages automated',
    ],
    protective_skills: ['Storytelling', 'Artistic judgment', 'Client management'],
    augmentation_tools: ['chatgpt', 'midjourney'],
    future_proof_skills: ['artistic_expression', 'creative_problem_solving', 'ai_tool_orchestration'],
    ai_impact_timeline: '2-5_years',
    evolution_path: 'Become a creative director who uses AI for rough cuts and focuses on story and style',
  },

  // Photographer (augmented)
  photographer: {
    career_id: 'photographer',
    automation_risk_score: 38,
    automation_risk_level: 'low',
    ai_relationship: 'ai_augmented',
    resilience_factors: [
      'Being present at the moment',
      'Directing subjects',
      'Creative vision',
      'Client relationship',
    ],
    vulnerability_factors: [
      'AI can generate images',
      'Auto-editing improving',
      'Stock photography pressure',
    ],
    protective_skills: ['Creative vision', 'People direction', 'Client experience'],
    augmentation_tools: ['midjourney', 'chatgpt', 'canva_ai'],
    future_proof_skills: ['artistic_expression', 'relationship_building', 'creative_problem_solving'],
    ai_impact_timeline: '5-10_years',
    evolution_path: 'Focus on experiences only humans can capture: events, portraits, documentary',
  },

  // Handyman (very resistant)
  handyman: {
    career_id: 'handyman',
    automation_risk_score: 18,
    automation_risk_level: 'very_low',
    ai_relationship: 'ai_resistant',
    resilience_factors: [
      'Every job is different',
      'Physical problem-solving',
      'Working in homes and tight spaces',
      'Customer trust and relationship',
    ],
    vulnerability_factors: [],
    protective_skills: ['Versatility', 'Problem-solving', 'Customer service'],
    augmentation_tools: ['chatgpt'],
    future_proof_skills: ['fine_motor_skills', 'creative_problem_solving', 'relationship_building'],
    ai_impact_timeline: 'unlikely',
  },

  // Social Media Manager (highly augmented)
  social_media_manager: {
    career_id: 'social_media_manager',
    automation_risk_score: 58,
    automation_risk_level: 'medium',
    ai_relationship: 'ai_augmented',
    resilience_factors: [
      'Brand voice understanding',
      'Community management',
      'Crisis response',
      'Trend interpretation',
    ],
    vulnerability_factors: [
      'Content generation automatable',
      'Scheduling automated',
      'Basic responses automated',
    ],
    protective_skills: ['Brand strategy', 'Community building', 'Crisis management'],
    augmentation_tools: ['chatgpt', 'canva_ai', 'jasper', 'grammarly'],
    future_proof_skills: ['creative_problem_solving', 'ai_tool_orchestration', 'relationship_building'],
    ai_impact_timeline: '2-5_years',
    evolution_path: 'Become a community strategist who oversees AI-generated content and focuses on authentic engagement',
  },

  // Driver (high risk for autonomous vehicles)
  driver: {
    career_id: 'driver',
    automation_risk_score: 72,
    automation_risk_level: 'high',
    ai_relationship: 'ai_vulnerable',
    resilience_factors: [
      'Customer interaction (for now)',
      'Handling edge cases',
      'Loading/unloading',
    ],
    vulnerability_factors: [
      'Autonomous vehicles developing',
      'Route optimization automated',
    ],
    protective_skills: ['Customer service', 'Safety judgment'],
    augmentation_tools: [],
    future_proof_skills: ['learning_agility', 'relationship_building'],
    ai_impact_timeline: '5-10_years',
    evolution_path: 'Transition to fleet management, logistics coordination, or specialized driving (hazmat, oversized)',
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get AI resilience profile for a career
 */
export function getCareerAIProfile(careerId: string): CareerAIProfile | null {
  return CAREER_AI_PROFILES[careerId] || null;
}

/**
 * Get automation risk label for display
 */
export function getAutomationRiskLabel(level: AutomationRiskLevel): string {
  switch (level) {
    case 'very_low':
      return 'Very Low Risk - AI-Resistant';
    case 'low':
      return 'Low Risk - Human-Essential';
    case 'medium':
      return 'Moderate Risk - AI-Augmented';
    case 'high':
      return 'High Risk - AI-Vulnerable';
    case 'very_high':
      return 'Very High Risk - At Risk of Automation';
    default:
      return 'Unknown';
  }
}

/**
 * Get AI relationship label for display
 */
export function getAIRelationshipLabel(relationship: AIRelationship): string {
  switch (relationship) {
    case 'ai_resistant':
      return 'AI-Resistant - Fundamentally human work';
    case 'ai_augmented':
      return 'AI-Augmented - Enhanced by AI tools';
    case 'ai_collaborative':
      return 'AI-Collaborative - Works alongside AI';
    case 'ai_vulnerable':
      return 'AI-Vulnerable - At risk of replacement';
    case 'ai_created':
      return 'AI-Created - New role in AI economy';
    default:
      return 'Unknown';
  }
}

/**
 * Get AI impact timeline label
 */
export function getAIImpactTimelineLabel(timeline: CareerAIProfile['ai_impact_timeline']): string {
  switch (timeline) {
    case 'immediate':
      return 'Already being impacted';
    case '2-5_years':
      return 'Significant changes in 2-5 years';
    case '5-10_years':
      return 'Changes expected in 5-10 years';
    case '10+_years':
      return 'Minimal impact for 10+ years';
    case 'unlikely':
      return 'Unlikely to be significantly automated';
    default:
      return 'Unknown';
  }
}

/**
 * Get color class for automation risk level
 */
export function getAutomationRiskColor(level: AutomationRiskLevel): string {
  switch (level) {
    case 'very_low':
      return 'green';
    case 'low':
      return 'lightgreen';
    case 'medium':
      return 'yellow';
    case 'high':
      return 'orange';
    case 'very_high':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Get AI tools for a career
 */
export function getAIToolsForCareer(careerId: string): AITool[] {
  const profile = CAREER_AI_PROFILES[careerId];
  if (!profile) return [];

  return profile.augmentation_tools
    .map(toolId => AI_TOOLS[toolId])
    .filter((t): t is AITool => t !== undefined);
}

/**
 * Get future-proof skills for a career
 */
export function getFutureProofSkillsForCareer(careerId: string): FutureProofSkill[] {
  const profile = CAREER_AI_PROFILES[careerId];
  if (!profile) return [];

  return profile.future_proof_skills
    .map(skillId => FUTURE_PROOF_SKILLS[skillId])
    .filter((s): s is FutureProofSkill => s !== undefined);
}

/**
 * Calculate overall AI resilience score for a career
 * Returns 0-100 (higher = more resilient/safe)
 */
export function calculateAIResilienceScore(careerId: string): {
  score: number;
  profile: CareerAIProfile | null;
  tools: AITool[];
  skills: FutureProofSkill[];
} {
  const profile = CAREER_AI_PROFILES[careerId];

  if (!profile) {
    return {
      score: 50, // Unknown careers get neutral score
      profile: null,
      tools: [],
      skills: [],
    };
  }

  // Invert automation risk to get resilience score
  const resilienceScore = 100 - profile.automation_risk_score;

  return {
    score: resilienceScore,
    profile,
    tools: getAIToolsForCareer(careerId),
    skills: getFutureProofSkillsForCareer(careerId),
  };
}

/**
 * Get AI augmentation recommendations for a career
 */
export function getAIAugmentationRecommendations(careerId: string): AIAugmentationRec | null {
  const profile = CAREER_AI_PROFILES[careerId];
  if (!profile) return null;

  const tools = getAIToolsForCareer(careerId);
  const skills = getFutureProofSkillsForCareer(careerId);

  // Estimate productivity boost based on AI relationship
  let productivityBoost = '10-20% more efficient';
  if (profile.ai_relationship === 'ai_augmented') {
    productivityBoost = '30-50% more efficient';
  } else if (profile.ai_relationship === 'ai_collaborative') {
    productivityBoost = '40-60% more efficient';
  }

  // Generate competitive advantage message
  let competitiveAdvantage = 'Stay relevant by learning AI tools';
  if (profile.automation_risk_level === 'very_low' || profile.automation_risk_level === 'low') {
    competitiveAdvantage = 'Your human skills are your advantage - AI tools make you even better';
  } else if (profile.automation_risk_level === 'high' || profile.automation_risk_level === 'very_high') {
    competitiveAdvantage = 'Learning AI tools is essential for career survival in this field';
  }

  return {
    career_id: careerId,
    tools,
    skills_to_develop: skills,
    productivity_boost_estimate: productivityBoost,
    competitive_advantage: competitiveAdvantage,
  };
}
